import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Dimensions, Metrics } from 'components/insights/constants';
import { formatResponseTime } from 'components/insights/formatting';
import AnalyticsResponseRollupCollection from 'components/insights/query/AnalyticsResponseRollupCollection';
import { colors } from 'styles/theme/colors';
import textStyles from 'styles/theme/textStyles';

import MetricDetailLayout from '../../shared/slideout/MetricDetailLayout';
import { readGrandTotal } from '../../shared/slideout/rollupHelpers';

import { StatTile } from './statTiles';
import useSlideoutAnalytics, { useSlideoutDimensionCounts } from './useSlideoutAnalytics';

type Props = {
  loading: boolean;
  rollups: AnalyticsResponseRollupCollection;
};

const HISTOGRAM_BUCKETS = [
  { label: '< 1hr', max: 1 * 3600 },
  { label: '1-2hr', max: 2 * 3600 },
  { label: '2-3hr', max: 3 * 3600 },
  { label: '3-4hr', max: 4 * 3600 },
  { label: '4-5hr', max: 5 * 3600 },
  { label: '5-6hr', max: 6 * 3600 },
  { label: '6-7hr', max: 7 * 3600 },
  { label: '7-8hr', max: 8 * 3600 },
  { label: '8-9hr', max: 9 * 3600 },
  { label: '9-10hr', max: 10 * 3600 },
  { label: '10+ hr', max: Infinity },
];

export default function ResponseTimeDetailHeader({ loading, rollups }: Props) {
  const { fastestResponder, slowestResponder, topReceivingLoading } = useSlideoutAnalytics();

  const {
    names: histogramSeconds,
    counts: histogramCounts,
    loading: histogramLoading,
  } = useSlideoutDimensionCounts(Dimensions.RESPONSE_TIME_SECONDS, 'referral-slideout-response-time-histogram');

  const avgResponseTime = readGrandTotal(rollups, Metrics.RESPONSE_TIME_SECONDS_AVERAGE);

  const { histogramSeries, respondedCount } = useMemo(() => {
    const buckets = HISTOGRAM_BUCKETS.map(() => 0);
    let total = 0;
    histogramSeconds.forEach((raw, i) => {
      const value = Number(raw);
      if (!Number.isFinite(value)) return;
      const count = histogramCounts[i] ?? 0;
      const idx = HISTOGRAM_BUCKETS.findIndex((b) => value < b.max);
      const target = idx === -1 ? HISTOGRAM_BUCKETS.length - 1 : idx;
      buckets[target] += count;
      total += count;
    });
    return { histogramSeries: buckets, respondedCount: total };
  }, [histogramSeconds, histogramCounts]);

  const chartOptions = useMemo<Highcharts.Options>(
    () => ({
      chart: { type: 'column', style: { fontFamily: textStyles.base.fontFamily }, height: 200 },
      accessibility: { enabled: false },
      credits: { enabled: false },
      title: { text: undefined },
      legend: {
        enabled: true,
        align: 'center',
        verticalAlign: 'bottom',
        symbolRadius: 2,
        itemStyle: { fontSize: '12px', color: colors.black75, fontWeight: 'normal' },
      },
      xAxis: {
        categories: HISTOGRAM_BUCKETS.map((b) => b.label),
        lineColor: colors.black10,
        tickLength: 0,
        labels: { style: { fontSize: '10px', color: colors.black50 } },
      },
      yAxis: {
        title: { text: undefined },
        gridLineWidth: 0,
        labels: { style: { fontSize: '10px', color: colors.black50 } },
      },
      tooltip: {
        backgroundColor: colors.black75,
        style: { color: colors.white },
        padding: 12,
        useHTML: true,
        formatter: function () {
          const count = Number(this.y);
          const noun = count === 1 ? 'referral' : 'referrals';
          return `<b>${count} ${noun}</b><br/><span style="color:${this.color}">●</span> ${this.series.name}: <b>${this.key}</b>`;
        },
      },
      plotOptions: { column: { borderRadius: 2, pointPadding: 0.05, groupPadding: 0.08 } },
      series: [
        {
          type: 'column',
          name: 'Response Time',
          color: colors.blue100,
          data: histogramSeries.map((y, i) => {
            const t = HISTOGRAM_BUCKETS.length === 1 ? 0 : i / (HISTOGRAM_BUCKETS.length - 1);
            const opacity = 1 - t * 0.8;
            return { y, color: `rgba(49, 127, 194, ${opacity.toFixed(2)})` };
          }),
        },
      ],
    }),
    [histogramSeries]
  );

  const tiles = (
    <>
      <StatTile
        label='Avg. Response Time'
        loading={loading}
        primary={formatResponseTime(avgResponseTime)}
        addendum={respondedCount > 0 ? `Based on ${respondedCount.toLocaleString()} referrals` : undefined}
      />
      <StatTile
        label='Fastest Responder'
        loading={topReceivingLoading}
        primary={fastestResponder?.name ?? '—'}
        size='medium'
        addendum={fastestResponder ? formatResponseTime(fastestResponder.value) : undefined}
      />
      <StatTile
        label='Slowest Responder'
        loading={topReceivingLoading}
        primary={slowestResponder?.name ?? '—'}
        size='medium'
        addendum={slowestResponder ? formatResponseTime(slowestResponder.value) : undefined}
      />
    </>
  );

  return (
    <MetricDetailLayout
      tiles={tiles}
      chartTitle='Response Time Distribution'
      chartLoading={histogramLoading}
      isEmpty={respondedCount === 0}
      emptyMessage='No response time distribution to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
